import React, { useState, useEffect, useRef } from 'react';
import { 
  FiX, 
  FiSave, 
  FiCamera, 
  FiTrash2, 
  FiUpload,
  FiUser,
  FiTag,
  FiAlertCircle
} from 'react-icons/fi';
import defectsApi, { DefectPin } from '../../services/DefectsApiService';
import './DefectModal.css';

interface DefectModalProps {
  defect: DefectPin | null;
  onSave: (defect: Partial<DefectPin> | DefectPin) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface FormErrors {
  title?: string;
  description?: string;
  severity?: string;
  trade?: string;
  assignedTo?: string;
}

const DefectModal: React.FC<DefectModalProps> = ({
  defect,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<Partial<DefectPin>>({
    title: '',
    description: '',
    severity: 'medium',
    status: 'open',
    assignedTo: '',
    trade: '',
    category: '',
    photos: []
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize form data
  useEffect(() => {
    if (defect) {
      setFormData({
        ...defect,
        photos: defect.photos || []
      });
    }
  }, [defect]);

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.severity) {
      newErrors.severity = 'Severity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      updatedAt: new Date().toISOString(),
      ...(defect?.id ? {} : { createdAt: new Date().toISOString() })
    };

    onSave(submitData);
  };

  // Handle input changes
  const handleInputChange = (field: keyof DefectPin, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhotos(true);

    try {
      const uploadPromises = Array.from(files).map((file) => defectsApi.uploadPhoto(file));

      const uploadedUrls = await Promise.all(uploadPromises);

      setFormData(prev => ({
        ...prev,
        photos: [...(prev.photos || []), ...uploadedUrls]
      }));

    } catch (error) {
      console.error('Photo upload failed:', error);
      alert('Failed to upload photos. Please try again.');
    } finally {
      setUploadingPhotos(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove photo
  const handleRemovePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos?.filter((_, i) => i !== index) || []
    }));
  };

  // Predefined options
  const severityOptions = [
    { value: 'low', label: 'Low Priority', color: '#16a34a' },
    { value: 'medium', label: 'Medium Priority', color: '#d97706' },
    { value: 'high', label: 'High Priority', color: '#ea580c' },
    { value: 'critical', label: 'Critical', color: '#dc2626' }
  ];

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' }
  ];

  const tradeOptions = [
    'General Contractor',
    'Electrical',
    'Plumbing',
    'HVAC',
    'Flooring',
    'Painting',
    'Carpentry',
    'Roofing',
    'Insulation',
    'Drywall',
    'Concrete',
    'Landscaping'
  ];

  const categoryOptions = [
    'Structural',
    'Mechanical',
    'Electrical',
    'Plumbing',
    'Finishes',
    'Safety',
    'Code Compliance',
    'Cosmetic',
    'Functional',
    'Documentation'
  ];

  return (
    <div className="defect-modal-overlay" onClick={onCancel}>
      <div className="defect-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            {defect?.id ? 'Edit Defect' : 'Create New Defect'}
          </h2>
          <button 
            className="modal-close-btn"
            onClick={onCancel}
            type="button"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Title */}
            <div className="form-group full-width">
              <label className="form-label required">
                <FiTag size={16} />
                Title
              </label>
              <input
                type="text"
                className={`form-input ${errors.title ? 'error' : ''}`}
                value={formData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Brief description of the defect"
                maxLength={100}
              />
              {errors.title && (
                <span className="error-message">
                  <FiAlertCircle size={14} />
                  {errors.title}
                </span>
              )}
            </div>

            {/* Description */}
            <div className="form-group full-width">
              <label className="form-label required">
                Description
              </label>
              <textarea
                className={`form-textarea ${errors.description ? 'error' : ''}`}
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Detailed description of the defect, including location and requirements for resolution"
                rows={4}
                maxLength={500}
              />
              <div className="textarea-counter">
                {(formData.description || '').length}/500
              </div>
              {errors.description && (
                <span className="error-message">
                  <FiAlertCircle size={14} />
                  {errors.description}
                </span>
              )}
            </div>

            {/* Severity */}
            <div className="form-group">
              <label className="form-label required">
                Severity
              </label>
              <select
                className={`form-select ${errors.severity ? 'error' : ''}`}
                value={formData.severity || 'medium'}
                onChange={(e) => handleInputChange('severity', e.target.value)}
              >
                {severityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.severity && (
                <span className="error-message">
                  <FiAlertCircle size={14} />
                  {errors.severity}
                </span>
              )}
            </div>

            {/* Status */}
            <div className="form-group">
              <label className="form-label">
                Status
              </label>
              <select
                className="form-select"
                value={formData.status || 'open'}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Trade */}
            <div className="form-group">
              <label className="form-label">
                Trade
              </label>
              <select
                className="form-select"
                value={formData.trade || ''}
                onChange={(e) => handleInputChange('trade', e.target.value)}
              >
                <option value="">Select Trade</option>
                {tradeOptions.map(trade => (
                  <option key={trade} value={trade}>
                    {trade}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div className="form-group">
              <label className="form-label">
                Category
              </label>
              <select
                className="form-select"
                value={formData.category || ''}
                onChange={(e) => handleInputChange('category', e.target.value)}
              >
                <option value="">Select Category</option>
                {categoryOptions.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Assigned To */}
            <div className="form-group">
              <label className="form-label">
                <FiUser size={16} />
                Assigned To
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.assignedTo || ''}
                onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                placeholder="Person or company responsible"
              />
            </div>

            {/* Photos Section */}
            <div className="form-group full-width">
              <label className="form-label">
                <FiCamera size={16} />
                Photos
              </label>

              {/* Photo Upload */}
              <div className="photo-upload-section">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="photo-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhotos}
                >
                  {uploadingPhotos ? (
                    <>
                      <div className="loading-spinner small"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FiUpload size={16} />
                      Add Photos
                    </>
                  )}
                </button>
              </div>

              {/* Photo Grid */}
              {formData.photos && formData.photos.length > 0 && (
                <div className="photos-grid">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="photo-item">
                      <img
                        src={photo}
                        alt={`Defect ${index + 1}`}
                        className="photo-thumbnail"
                      />
                      <button
                        type="button"
                        className="photo-remove-btn"
                        onClick={() => handleRemovePhoto(index)}
                        title="Remove photo"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Modal Actions */}
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || uploadingPhotos}
            >
              {loading ? (
                <>
                  <div className="loading-spinner small"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FiSave size={16} />
                  {defect?.id ? 'Update Defect' : 'Create Defect'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DefectModal;
